import { useCallback, useEffect, useState } from 'react';
import { Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import PageMeta from 'components/PageMeta/PageMeta';
import RadioCards from 'components/RadioCards/RadioCards';
import { familyService } from 'services';
import { EMPLOYMENT_STATUS } from 'constants/index';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { MILITARY_SERVICE_OPTIONS } from '../sponsor/FamilyApplicationStart';

export function FamilyAppEmploymentActivities({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // Application
  const isCompletedBySponsor = application?.completedBySponsor === true;

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (newApplication) => {
        // Use getNextStep to determine the next step with the updated application
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES, newApplication, stepConfig);
        setStep({
          applicationId: newApplication.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const _spouse = application?.additionalParents?.[0];
        const _isEmployed = values.employmentStatus === EMPLOYMENT_STATUS.EMPLOYED;

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            employmentStatus: values.employmentStatus,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_EMPLOYMENT_DETAILS step
            fullTimeEmployee: _isEmployed ? _spouse?.fullTimeEmployee : null,
            employmentDetails: _isEmployed ? _spouse?.employmentDetails : null,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_MILITARY_EMPLOYMENT step
            militaryEmployee: _isEmployed ? _spouse?.militaryEmployee : null,
            militaryDetails: _isEmployed ? _spouse?.militaryDetails : null,
            // FAMILY-APPS__DATA_CLEANING => SPOUSE_SELF_EMPLOYMENT step
            selfEmployed: _isEmployed ? _spouse?.selfEmployed : null,
            selfEmploymentDetails: _isEmployed ? _spouse?.selfEmploymentDetails : null,
          },
        );

        const newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(newApplication);
        _goToNextStep(newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial values
  useEffect(() => {
    if (application?.additionalParents?.[0]) {
      const currentEmploymentStatus = application?.additionalParents?.[0]?.employmentStatus;
      const isDualMilitary = application?.serviceMember === MILITARY_SERVICE_OPTIONS.BOTH;

      form.setFieldsValue({
        employmentStatus: currentEmploymentStatus ?? (isDualMilitary ? EMPLOYMENT_STATUS.EMPLOYED : undefined),
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Spouse Employment Status" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Does ${getOtherAdultFirstName(application?.additionalParents?.[0])} currently work?`
                : 'Do you currently work?'
            }
            guidance="We'll ask about school or training next"
            className="my-12"
          />

          <Form.Item
            name="employmentStatus"
            rules={[{ required: true, message: 'Please select an employment status' }]}
          >
            <RadioCards
              options={[
                {
                  label: `Yes, ${isCompletedBySponsor ? "they're" : "I'm"} working`,
                  value: EMPLOYMENT_STATUS.EMPLOYED,
                },
                {
                  label: 'No, not working',
                  value: EMPLOYMENT_STATUS.NONEMPLOYED,
                },
              ]}
            />
          </Form.Item>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.EMPLOYMENT_ACTIVITIES,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

export default FamilyAppEmploymentActivities;
